import { useEffect, useState } from 'react';
import api from '../services/api';
import { toast } from 'sonner';
import {
  HeroSection,
  FeaturedProductsSection,
  ThreeStepsSection,
  CategoriesSection,
  ReviewsSection,
  CtaBannerSection,
  TextBlockSection,
  ImageBannerSection,
} from '../components/home/HomeSections';

const Home = () => {
  const [layout, setLayout] = useState([]);
  const [data, setData] = useState({ categories: [], reviews: [], reviewPlatforms: [], featuredProducts: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAll = async () => {
      try {
        // Step 1: fetch the layout config
        const layoutRes = await api.get('/home-layout');
        const sections = (layoutRes.data?.sections || []).filter((s) => s.enabled !== false);
        setLayout(sections);

        // Step 2: figure out which auxiliary data we need based on section types
        const needCategories = sections.some((s) => s.type === 'categories');
        const needReviews = sections.some((s) => s.type === 'reviews');
        const featuredSections = sections.filter((s) => s.type === 'featured-products');

        const promises = [];
        if (needCategories) promises.push(api.get('/products/categories'));
        else promises.push(Promise.resolve({ data: [] }));

        if (needReviews) {
          promises.push(api.get('/reviews'));
          promises.push(api.get('/review-platforms'));
        } else {
          promises.push(Promise.resolve({ data: [] }));
          promises.push(Promise.resolve({ data: { platforms: [] } }));
        }

        const [catRes, revRes, platRes] = await Promise.all(promises);

        // Step 3: load featured products per section (manual vs auto)
        const featuredProducts = {};
        if (featuredSections.length > 0) {
          // Fetch all products once and slice per section
          const allProducts = (await api.get('/products')).data || [];
          for (const sec of featuredSections) {
            if (sec.mode === 'manual' && sec.product_ids?.length) {
              featuredProducts[sec.id] = sec.product_ids
                .map((pid) => allProducts.find((p) => p.product_id === pid))
                .filter(Boolean)
                .slice(0, sec.count || 4);
            } else {
              // auto strategy
              const sorted = [...allProducts];
              if (sec.auto_strategy === 'featured') {
                sorted.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
              } else if (sec.auto_strategy === 'popular') {
                sorted.sort((a, b) => (b.order_count || 0) - (a.order_count || 0));
              } else {
                // newest (default)
                sorted.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
              }
              featuredProducts[sec.id] = sorted.slice(0, sec.count || 4);
            }
          }
        }

        setData({
          categories: catRes.data,
          reviews: revRes.data,
          reviewPlatforms: platRes.data.platforms || [],
          featuredProducts,
        });
      } catch (err) {
        console.debug('[home] load failed:', err?.message);
        toast.error('Kunde inte ladda startsidan');
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Laddar...</div>;
  }

  const renderSection = (section) => {
    switch (section.type) {
      case 'hero':
        return <HeroSection key={section.id} cfg={section} />;
      case 'featured-products':
        return <FeaturedProductsSection key={section.id} cfg={section} products={data.featuredProducts[section.id] || []} />;
      case 'three-steps':
        return <ThreeStepsSection key={section.id} cfg={section} />;
      case 'categories':
        return <CategoriesSection key={section.id} cfg={section} categories={data.categories} />;
      case 'reviews':
        return <ReviewsSection key={section.id} cfg={section} reviews={data.reviews} reviewPlatforms={data.reviewPlatforms} />;
      case 'cta-banner':
        return <CtaBannerSection key={section.id} cfg={section} />;
      case 'text-block':
        return <TextBlockSection key={section.id} cfg={section} />;
      case 'image-banner':
        return <ImageBannerSection key={section.id} cfg={section} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {layout.map(renderSection)}
    </div>
  );
};

export default Home;
